import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import Loading from '../../form/fields/Loading'
import uploadIcon from '../../assets/icons/Upload.svg'
import {
  DeleteMedPassDocuments,
  getPatientDocuments,
  UploadMedPassDocuments,
} from '../../services/MembershipDetailsService'
import './medPassMembership.css'
import { viewBase64File, downloadBase64File } from './utils'
import { constants } from '../ProRithm/Constants'
import { docMsg, Documents, DocuView } from './models'

interface UploadDocsProps {
  patientId: number | string
  muhid?: string | number
}

const UploadMedPassDocs: React.FC<UploadDocsProps> = ({ patientId, muhid }) => {
  const docsRef = useRef<HTMLInputElement | null>(null)
  const [uploadedDoc, setUploadedDoc] = useState<any[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedDocUrl, setSelectedDocUrl] = useState('')
  const [loading, setLoading] = useState(false)

  const fetchPatientDocs = useCallback(async () => {
    setLoading(true)
    const response = await getPatientDocuments(muhid)
    if (response?.success) {
      setUploadedDoc(response?.data)
      setLoading(false)
    } else {
      setUploadedDoc([])
      setLoading(false)
    }
  }, [muhid])

  useEffect(() => {
    fetchPatientDocs()
  }, [patientId, fetchPatientDocs])

  const handleUploadDoc = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files?.length) return
    setLoading(true)
    const formData = new FormData()
    formData.append('file', files[0])
    const response = await UploadMedPassDocuments(muhid, formData)
    if (response?.success) {
      toast.success('success')
      fetchPatientDocs()
    } else {
      toast.error('Error Occurred')
    }
    setLoading(false)
  }

  const handleDeleteDocument = useCallback(
    async (fileName: string) => {
      const res = await DeleteMedPassDocuments(patientId, fileName)
      if (res?.success) {
        toast.success(docMsg)
        fetchPatientDocs()
      } else {
        toast.error(res.message)
      }
    },
    [patientId, fetchPatientDocs]
  )

  const DocumentModal = () => {
    const handleClose = () => {
      setIsModalOpen(false)
      setSelectedDocUrl('')
    }
    return (
      <div className={`document-modal ${isModalOpen ? 'open' : ''}`}>
        {selectedDocUrl?.endsWith('.pdf') && (
          <div className='document-modal-content'>
            <button className='close-modal-button' onClick={handleClose}>
              {constants?.close}
            </button>
            <iframe
              src={selectedDocUrl}
              width='100%'
              height='100%'
              title='Document Preview'
            />
          </div>
        )}
      </div>
    )
  }

  const openFileSelector = () => {
    docsRef?.current?.click()
  }

  return (
    <div className='DocContainer'>
      <div className='mainCard'>
        <h3>
          <div className='docViewBody'>
            <div className='docUploadBtn'>
              <div className='docBody'>
                <label htmlFor='docUrl'>
                  <span className='docUrl'>{Documents}</span>
                </label>
                <img
                  src={uploadIcon}
                  alt='upload button'
                  title='Upload Documents'
                  onClick={openFileSelector}
                  className='upload-icon'
                />
              </div>
              <input
                id='docUrl'
                ref={docsRef}
                type='file'
                accept='image/*, application/pdf'
                className='docUrl'
                onChange={handleUploadDoc}
              />
            </div>
          </div>
        </h3>
        <div className='docView'>
          <ul className='docListView'>
            {uploadedDoc?.length > 0 &&
              uploadedDoc?.map((doc: any, index: number) => {
                const serialNo = index + 1
                const fileName = doc.fileName?.split('/')?.[1]

                return (
                  <li className='docListItem' key={index}>
                    <div className='docItemInfo'>
                      <div className='docItemSerial'>{`${serialNo} - `}</div>
                      <div className='docItemTitle'>{fileName}</div>
                      <div className='docItemButtons'>
                        <button
                          className='docItemButton'
                          onClick={() => viewBase64File(doc.data)}
                        >
                          {DocuView?.Preview}
                        </button>
                        <button
                          className='docItemButton'
                          onClick={() =>
                            downloadBase64File(doc?.data, fileName)
                          }
                        >
                          {DocuView?.Download}
                        </button>
                        <button
                          className='docItemButton'
                          onClick={() => handleDeleteDocument(doc.fileName)}
                        >
                          {DocuView?.Delete}
                        </button>
                      </div>
                    </div>
                  </li>
                )
              })}
          </ul>
        </div>
      </div>
      {isModalOpen && <DocumentModal />}
      {loading && <Loading data-testid='loader' />}
    </div>
  )
}

export default UploadMedPassDocs
